/**
 * AURELIA — component-wishlist.js
 * <wishlist-button> and <wishlist-list>
 *
 * Storage: localStorage, keyed per shop so a shared browser cannot leak one
 * store's list into another. No account required, no app dependency, no
 * network request — the list is instant and works for logged-out visitors,
 * which is the overwhelming majority of wishlist users.
 *
 * For merchants who want cross-device persistence, `wishlist:changed` carries
 * the full list so a customer-metafield sync can be attached in one listener.
 */
(function () {
  'use strict';

  const { Element, emit, on, a11y } = window.Aurelia;

  const STORAGE_KEY = `aurelia:wishlist:${window.Shopify && window.Shopify.shop ? window.Shopify.shop : 'shop'}`;
  const LIMIT = 100;

  const store = {
    read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        // Private browsing in older Safari throws on access rather than
        // returning null. An empty list is the correct degradation.
        return [];
      }
    },

    write(list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, LIMIT)));
      } catch (error) {
        /* Quota exceeded or storage unavailable — the in-memory list still works. */
      }
      emit('wishlist:changed', { items: list });
    },

    has(handle) {
      return store.read().some((entry) => entry.handle === handle);
    },

    toggle(handle, id) {
      const list = store.read();
      const index = list.findIndex((entry) => entry.handle === handle);

      if (index > -1) {
        list.splice(index, 1);
        store.write(list);
        return false;
      }

      list.unshift({ handle, id, addedAt: Date.now() });
      store.write(list);
      return true;
    },
  };

  /* ---------------------------------------------------------------------
     Toggle button
     --------------------------------------------------------------------- */
  class WishlistButton extends Element {
    mount() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.handle = this.button.dataset.productHandle;
      this.productId = this.button.dataset.productId;
      this.label = this.querySelector('[data-wishlist-label]');
      this.addedLabel = this.button.dataset.labelAdded;
      this.removedLabel = this.button.dataset.labelRemoved;

      this.sync();

      this.listen(this.button, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const added = store.toggle(this.handle, this.productId);
        this.sync(added);
        a11y.announce(added ? this.addedLabel || '' : this.removedLabel || '');
      });

      // Several cards can show the same product; keep every copy in step.
      this.subscribe('wishlist:changed', () => this.sync());
    }

    sync(known) {
      const active = typeof known === 'boolean' ? known : store.has(this.handle);
      this.button.setAttribute('aria-pressed', String(active));

      const icon = this.button.querySelector('svg');
      if (icon) {
        // Swap outline for filled by toggling the path fill — cheaper than
        // re-rendering two icons and avoids a layout pass.
        icon.classList.toggle('icon--filled', active);
      }
    }
  }

  window.Aurelia.define('wishlist-button', WishlistButton);

  /* ---------------------------------------------------------------------
     List renderer (account page / wishlist drawer)
     --------------------------------------------------------------------- */
  class WishlistList extends Element {
    mount() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.countTarget = this.querySelector('[data-wishlist-count]');

      this.render();
      this.subscribe('wishlist:changed', () => this.render());
    }

    async render() {
      const items = store.read();

      if (this.countTarget) this.countTarget.textContent = items.length;

      const isEmpty = items.length === 0;
      if (this.emptyState) this.emptyState.hidden = !isEmpty;
      if (this.grid) this.grid.hidden = isEmpty;
      if (isEmpty || !this.grid) return;

      this.grid.setAttribute('aria-busy', 'true');

      // Each card is fetched as a rendered section so it is identical to a
      // card anywhere else in the theme — no duplicated client-side template.
      const sectionId = this.dataset.cardSection || 'wishlist-card';
      const cards = await Promise.all(
        items.map((entry) =>
          fetch(`/products/${entry.handle}?section_id=${sectionId}`)
            .then((response) => (response.ok ? response.text() : null))
            .catch(() => null)
        )
      );

      const fragment = document.createDocumentFragment();
      cards.forEach((html, index) => {
        if (!html) {
          // The product was deleted or unpublished — drop it from the list so
          // the customer is not left with a permanently broken entry.
          const list = store.read().filter((entry) => entry.handle !== items[index].handle);
          store.write(list);
          return;
        }
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const card = parsed.querySelector('.shopify-section > *') || parsed.body.firstElementChild;
        if (card) fragment.appendChild(card);
      });

      this.grid.innerHTML = '';
      this.grid.appendChild(fragment);
      this.grid.removeAttribute('aria-busy');

      emit('content:appended', { target: this.grid });
    }
  }

  window.Aurelia.define('wishlist-list', WishlistList);

  /* ---------------------------------------------------------------------
     Header count badge
     --------------------------------------------------------------------- */
  function syncHeaderCount() {
    const count = store.read().length;
    document.querySelectorAll('[data-wishlist-total]').forEach((node) => {
      node.textContent = count;
      node.toggleAttribute('hidden', count === 0);
    });
  }

  on('wishlist:changed', syncHeaderCount);
  syncHeaderCount();
})();
